import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/deploy/components/ui/Button';
import { Badge } from '@/deploy/components/ui/Badge';
import { Icon } from '@/deploy/components/ui/Icon';
import { Modal } from '@/deploy/components/ui/Modal';
import { networkAdminService } from '@/deploy/services/network.service';
import type { CloudflareZone, ImportReport, NetworkJob, NetworkStatus } from '@/deploy/types/network';
import { useNotificationsStore } from '@/stores/notifications.store';
import { JobModal } from '../JobModal';
import { errorMessage } from '../networkFormat';
import { ProjectDomainsCard } from './ProjectDomainsCard';
import { CloudflareCard, NodesCard, TargetsCard } from './InfraCards';
import settingsStyles from '@/deploy/pages/Settings.module.scss';
import styles from '../Network.module.scss';

/**
 * Paramètres > Réseau : ce qui est commun à tous les projets (admin). Le réseau propre à un projet
 * (domaines, certificats) est dans la catégorie Réseau de la barre latérale.
 */
export function NetworkSettingsSection() {
  const notify = useNotificationsStore();
  const [status, setStatus] = useState<NetworkStatus | null>(null);
  const [zones, setZones] = useState<CloudflareZone[]>([]);
  const [job, setJob] = useState<{ job: NetworkJob; title: string } | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const loadZones = useCallback(() => {
    networkAdminService.zones().then(setZones).catch(() => setZones([]));
  }, []);

  useEffect(() => {
    networkAdminService.status().then((s) => {
      setStatus(s);
      if (s.reachable) loadZones();
    }).catch((e) => setStatus({ configured: false, reachable: false, error: errorMessage(e) }));
  }, [loadZones]);

  const action = async (key: string, title: string, fn: () => Promise<NetworkJob>) => {
    setBusy(key);
    try {
      setJob({ job: await fn(), title });
    } catch (e) {
      notify.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const testSlack = async () => {
    setBusy('slack');
    try {
      const r = await networkAdminService.testNotification();
      if (r.sent) notify.success('Message de test envoyé sur Slack');
      else notify.error('Slack a refusé le message');
    } catch (e) {
      notify.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const s = status?.settings;
  return (
    <section className={settingsStyles.section}>
      <header className={settingsStyles.sectionHeader}>
        <div>
          <h2>
            <Icon name="zap" size={16} /> Réseau
            {status && (status.reachable
              ? <Badge tone="success" dot>ssl-manager joignable</Badge>
              : <Badge tone="danger" dot>ssl-manager injoignable</Badge>)}
          </h2>
          <p>Reverse proxy, certificats et DNS, communs à tous les projets.</p>
        </div>
        {status?.reachable && (
          <div className={styles.toolbar}>
            <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={12} />} loading={busy === 'reload'}
              onClick={() => action('reload', 'Reload nginx', networkAdminService.reloadNginx)}>Recharger nginx</Button>
            <Button variant="ghost" size="sm" leftIcon={<Icon name="clock" size={12} />} loading={busy === 'renew'}
              onClick={() => action('renew', 'Vérification des renouvellements', networkAdminService.renewalCheck)}>Vérifier les renouvellements</Button>
            <Button variant="ghost" size="sm" leftIcon={<Icon name="download" size={12} />} onClick={() => setImportOpen(true)}>
              Importer l'existant
            </Button>
          </div>
        )}
      </header>

      {status && !status.reachable && <div className={styles.error}>{status.error}</div>}

      {s && (
        <div className={styles.statusLine}>
          <span>CNAME vers <code>{s.proxyCnameTarget}</code></span>
          <span>·</span>
          <span>Renouvellement {s.defaultRenewBeforeDays} j avant expiration</span>
          <span>·</span>
          <span>{s.acmeDirectory.includes('staging') ? <Badge tone="warning">Let's Encrypt staging</Badge> : "Let's Encrypt"}</span>
          <span>·</span>
          {s.slackConfigured
            ? <Button variant="ghost" size="sm" loading={busy === 'slack'} onClick={testSlack}>Tester Slack</Button>
            : <Badge tone="warning">Slack non configuré</Badge>}
        </div>
      )}

      {status?.reachable && (
        <div className={styles.settingsGrid}>
          <ProjectDomainsCard zones={zones} />
          <CloudflareCard onChanged={loadZones} />
          <NodesCard />
          <TargetsCard />
        </div>
      )}

      {importOpen && <ImportModal onClose={() => setImportOpen(false)} />}
      {job && (
        <JobModal job={job.job} title={job.title} fetchJob={networkAdminService.job} onClose={() => setJob(null)} />
      )}
    </section>
  );
}

/** Import des vhosts des anciens scripts : simulation d'abord, puis import réel. */
function ImportModal({ onClose }: { onClose: () => void }) {
  const notify = useNotificationsStore();
  const [report, setReport] = useState<ImportReport | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    networkAdminService.importNginx(true).then(setReport).catch((e) => setError(errorMessage(e))).finally(() => setBusy(false));
  }, []);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await networkAdminService.importNginx(false);
      setReport(r);
      setDone(true);
      notify.success(`${r.imported} vhost(s) importé(s)`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const toImport = report?.files.filter((f) => f.status === 'IMPORTED').length ?? 0;
  return (
    <Modal open onClose={onClose} title="Importer les vhosts existants" width="lg"
      subtitle={done ? 'Import terminé' : 'Simulation : rien n\'est encore écrit'}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Fermer</Button>
        {!done && <Button variant="primary" onClick={confirm} loading={busy} disabled={busy || toImport === 0}>Importer {toImport} vhost(s)</Button>}
      </>}>
      <div className={styles.form}>
        <div className={styles.info}>
          Chaque fichier devient un vhost « importé » : il n'est jamais modifié, seul son certificat est suivi et renouvelé.
          Synchronise les comptes Cloudflare avant, pour que les certificats passent en validation DNS.
        </div>
        {error && <div className={styles.error}>{error}</div>}
        {busy && !report && <span className={styles.muted}>Analyse des fichiers…</span>}
        {report && (
          <div className={styles.list}>
            {report.files.map((f) => (
              <div key={f.file} className={styles.item} style={{ padding: '8px 12px' }}>
                <Badge tone={f.status === 'IMPORTED' ? 'success' : f.status === 'FAILED' ? 'danger' : 'muted'}>
                  {f.status === 'IMPORTED' ? (done ? 'Importé' : 'À importer') : f.status === 'FAILED' ? 'Erreur' : 'Ignoré'}
                </Badge>
                <div className={styles.itemMain}>
                  <span className={styles.primary}>{f.domain ?? f.file.split('/').pop()}</span>
                  <span className={styles.muted}>{f.detail}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
